import type {
  LinkToFlowNode as LinkToFlowNodeType,
  GraphDetails,
} from 'src/types/chatbot';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {NodeEditorProps} from './node-editor-v2';

import produce from 'immer';

import * as React from 'react';
import {useApi} from 'src/hooks/useApi';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useChatbotStateContext} from '../chatbot-state-context';

import {createLinkToFlowNode} from '../utils';

import {Button} from 'src/designSystem2021Components/buttons';

import {Dropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';

import {useEditorState} from '../node-editor/reducer';

import css from './node-editor-v2.css';
import nodeCss from './link-to-node.css';


function validateNode(node: LinkToFlowNodeType): boolean {
  return node.additional_configs_json.target_flow_id != null;
}

export function LinkToFlowNode({
  graph,
  onCancel,
  readOnly: _ro,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps) {
  const {t} = useLabelTranslation();
  const {state, dispatch} = useChatbotStateContext();
  // this is no longer a list of flows without externals fetched, so you can only really rely on
  // table-embedded data like name/id/description/
  // It is now an array of {[flow_id: number]: string} where each key
  // maps to a flow name
  const linkableFlowsRequest = useApi<
    {
      [id: number]: string;
    }[]
  >(`chatbot/flows/${graph.id}/list-linkable-flows`);

  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const defaultNode =
    state.editingNode !== null && editingNodeData?.type === 'link_to_flow'
      ? editingNodeData
      : createLinkToFlowNode();

  const [editorState, editorDispatch] = useEditorState<LinkToFlowNodeType>({
    defaultNode,
  });

  const {node} = editorState;

  const setNode = (payload: LinkToFlowNodeType) => {
    editorDispatch({type: 'update', payload});
  };

  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  // TODO(marcos): fix this
  const isValidNode = validateNode(node);

  const handleSaveNode = () => {
    if (!isValidNode) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  const linkableFlows = React.useMemo(() => {
    if (linkableFlowsRequest.result == null) {
      return {options: []};
    } else {
      const options: MenuOption[] = linkableFlowsRequest.result.flatMap(
        (flow) => {
          return Object.entries(flow).map(([id, name]: [any, any]) => ({
            key: String(id),
            label: String(name),
          }));
        },
      );
      return {options, allowSearch: true};
    }
  }, [linkableFlowsRequest.result]);

  if (linkableFlowsRequest.isLoading || linkableFlowsRequest.result == null) {
    return null;
  }

  const selectedOption = linkableFlows.options.find(
    (opt) => opt.key === String(node.additional_configs_json.target_flow_id),
  );

  return (
    <div className={css.questionNodeContainer}>
      <div className={nodeCss.linkToFlowContent}>
        <p>{`${t(
          'Select_from_the_list_of_applicable_flows_you_can_link_to_this_flow_This_will_route_users_to_this_flow_within_this_conversation',
          'Select from the list of applicable flows you can link to this flow. This will route users to this flow within this conversation',
        )}.`}</p>
        <Dropdown
          label={`${t('Select_Flow', 'Select Flow')}:`}
          // placeholder="Flows"
          placeholder={`${t('Select', 'Select')}...`}
          menu={linkableFlows}
          disabled={linkableFlows.options.length === 0}
          onChange={(opt) => {
            const next = produce(node, (draft) => {
              draft.additional_configs_json.target_flow_id = Number(opt.key);
            });
            setNode(next);
          }}
          dropdownInputText={selectedOption ? selectedOption.label : undefined}
          // @ts-expect-error - TS2769 - No overload matches this call.
          selectedKeys={selectedOption ? [selectedOption.key] : []}
        />
      </div>

      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={hasSubmitted ? true : !isValidNode}
        >
          {isSavingNode ? `${t('Saving', 'Saving')}...` : t('Save', 'Save')}
        </Button>
      </div>
    </div>
  );
}
